/**
 * @file: chatMessagePreview.ts
 *
 * Client-side plain-text preview for a chat message payload — the inbox
 * row's last-message line and the live-update reducer both render through
 * this. The FRONTEND owns this rendering: the server ships the structured
 * `payload` (+ a `preview` string only as a push/search/older-client
 * fallback), and we format it here so the row can evolve (audio duration,
 * file name, card subject, …) without a server change.
 *
 * Viewer-aware for system notifications ("你邀请…") via chatNotificationText.
 */

import { ReportContentType } from '@euler/model/ReportContentType';
import { ChatMessagePayload, ChatTaskCardBase } from '@euler/model/chat';
import { IdType } from '@euler/model/common';
import { chatNotificationText } from './chatNotificationText';
import { markdownToPlain } from './markdownToPlain';

const PREVIEW_CAP = 150;

export function chatMessagePreview(
  payload: ChatMessagePayload | null | undefined,
  myPlatformUserId: IdType,
): string | null {
  if (!payload) return null;
  // System notifications render a localized, viewer-aware line ("你邀请…").
  if (payload.type === 'notification') {
    return chatNotificationText(payload, myPlatformUserId);
  }
  // Text/AI read from content (clamped); markdown is flattened to plain.
  if (payload.type === 'text') return clampPreview(payload.text ?? '');
  if (payload.type === 'markdown') {
    return clampPreview(markdownToPlain(payload.markdown ?? ''));
  }
  switch (payload.type) {
    case 'media':
      return payload.kind === 'video' ? '[视频]' : '[图片]';
    case 'medias':
      if (payload.items.length === 1) {
        return payload.items[0].kind === 'video' ? '[视频]' : '[图片]';
      }
      return '[图片/视频]';
    case 'audio':
      return `[语音] ${Math.round((payload.durationMs ?? 0) / 1000)}″`;
    case 'file':
      return payload.filename ? `[文件] ${payload.filename}` : '[文件]';
    case 'order_card':
      return `[订单] ${cardPlate(payload)}`;
    case 'report_card':
      return `[${reportLabel(payload.reportType)}] ${cardPlate(payload)}`;
    case 'service_record_card':
      return `[服务记录] ${cardPlate(payload)}`;
    case 'contact_card':
      return payload.name
        ? `[联系人] ${payload.name}${payload.phone ? ` ${payload.phone}` : ''}`
        : '[联系人]';
    default:
      // A payload type the client doesn't render yet (e.g. extended_json):
      // fall back to the server's preview string rather than a blank line.
      return (payload as { _s?: string })._s ?? '[消息]';
  }
}

/** Plate (or order no fallback) for a task card preview. */
function cardPlate(card: ChatTaskCardBase): string {
  return card.vehicle?.licensePlateNo?.trim() || card.orderNo || '';
}

function clampPreview(s: string): string {
  return s.length <= PREVIEW_CAP ? s : s.slice(0, PREVIEW_CAP - 1) + '…';
}

function reportLabel(reportType: ReportContentType | undefined): string {
  switch (reportType) {
    case ReportContentType.PreInspection:
      return '预检报告';
    case ReportContentType.Construction:
      return '施工报告';
    case ReportContentType.DeliveryCheck:
      return '交车报告';
    case ReportContentType.SamplingCheck:
      return '抽检报告';
    default:
      return '检测报告';
  }
}
